import { useEffect, useRef, useState } from 'react';
import type { Lang } from '../i18n';
import './LangToggle.css';
import Icon from './Icon';

// 语言开关 —— **哑组件**:自己不持有语言状态,只收 { lang, onChange }。
//
// ★ 为什么不复用网页那份 LangToggle
//   那一份 import 了网页的 i18n 单例(localStorage + useSyncExternalStore),
//   桌面没有那个单例。把状态提到外面,组件就两端都能用 ——
//   网页接自己的 store,桌面接自己的 localStorage。**样式和结构只有一份。**
//   (样式直接从 frontend/src/components/LangToggle.css 搬过来,一个像素都没改。)

const LABELS: Record<Lang, { short: string; long: string }> = {
  zh: { short: '中', long: '中文' },
  en: { short: 'EN', long: 'English' },
};

export default function LangToggle({ lang, onChange }: { lang: Lang; onChange: (l: Lang) => void }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="lang-toggle-wrap" ref={wrapRef}>
      <button
        type="button"
        className="lang-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={lang === 'zh' ? '切换语言' : 'Switch language'}
      >
        {LABELS[lang].short}
        <span className="lang-toggle-caret" aria-hidden="true"><Icon name="chevron-down" size={11} /></span>
      </button>
      {open && (
        <div className="lang-toggle-dropdown" role="menu">
          {(Object.keys(LABELS) as Lang[]).map((l) => (
            <button
              key={l}
              type="button"
              role="menuitem"
              className="lang-toggle-item"
              aria-current={lang === l}
              onClick={() => { onChange(l); setOpen(false); }}
            >
              {LABELS[l].long}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
